// Agent fleet — the platform model-routing registry + the heavy lane's swarm.
//
// Routing table: every LLM CONSUMER in the platform and the model it runs on,
// grouped by WHERE it runs (in-product chat / the remote agent fleet / backend
// tasks) — the fleet section is where a vendor switch happens. Rows are named
// after what a user sees in the product (Board builds, chat, digests), not
// after internals; "Runs on" spells out the vendor with its billing model.
//
// Status closes the loop for fleet rows: the dispatcher's heartbeat carries
// the routes the pod actually applies (fleet_status.routes), so a row reads
// "applied by pod" / "pending" / "no live pod" / "drift" instead of a flat
// "active". Chat/backend consumers read the registry on next use → "active".
//
// The three chat personas share one default in practice; the table shows them
// as ONE row (writing to all three chat_* routes) until someone customizes
// per persona — the data model is unchanged, only the presentation folds.
//
// Access (like Cortex Audit): nav + route are mindshine-staff-gated (domain,
// any role); /api/fleet accepts any authenticated user (backend/modules/fleet).
//
// "Fleet workers": the dispatcher's heartbeat rows (fleet_status) — alive /
// last seen, the routes the pod applies, and per-vendor subscription limit
// windows (the same numbers Claude Code's /usage shows), rendered generically
// from a vendor-neutral `windows` list so a vendor switch needs no UI change.
//
// "Working now — heavy lane" (components/fleet/HeavySwarm): the live swarm of
// heavy app-request jobs, polled every HEAVY_POLL_MS from GET /api/fleet/heavy
// (read from app_request + fleet_status — no fleet-side change). The registry
// itself loads once and on Reload.
import React, { useEffect, useState } from "react";
import { getFleetModels, getFleetHeavy, setModelRoute } from "../services/fleet";
import HeavySwarm from "../components/fleet/HeavySwarm";
import Hint from "../components/fleet/Hint";
import { fmtDur, fmtAgo, fmtClockAt, fmtQuota } from "../components/fleet/format";
import {
  Bot, Loader2, MessageSquare, Server, Zap, CircleCheck, CircleDashed, RefreshCw,
  Hammer, AlertTriangle, Radio, ChevronDown, ChevronRight,
} from "lucide-react";

// Heavy-lane refresh cadence — the worker ticks every ~5s, so polling faster
// buys nothing; Audit/Docs poll their job lists at 4–8s.
const HEAVY_POLL_MS = 5000;

// Where a consumer runs. The `match` picks consumers by prefix so consumers
// added server-side land in the right section (or in "Other") unlisted here.
const SECTIONS = [
  { key: "chat", title: "In-product chat", note: "a new chat starts on this model; people can switch per chat", match: (c) => c.startsWith("chat_") },
  { key: "fleet", title: "Agent fleet (remote workers)", note: "switch the model or vendor here — workers pick it up within a minute", match: (c) => c.startsWith("fleet_") },
  { key: "backend", title: "Backend tasks", note: "read on next call", match: (c) => c.startsWith("backend_") },
  { key: "other", title: "Other", note: "", match: () => true },
];

// Display metadata per consumer — product words, not internals. Unknown
// consumers still render with a plain label so the page never hides a route.
// `exclude`: catalog ids this consumer must not be offered (fast analyst's
// SQL quality floor is Sonnet — the script documents it, the UI enforces it).
const CONSUMER_META = {
  chat_governance: { label: "Main chat (orchestrator)", icon: MessageSquare, hint: "Assistant panel on every page; delegates keep their built-in default" },
  chat_engine: { label: "Engine chat", icon: MessageSquare, hint: "Flow / engine surfaces" },
  chat_analyst: { label: "Analyst chat", icon: MessageSquare, hint: "Analyst panel in shared apps" },
  fleet_persona: { label: "Builds & persona jobs", icon: Hammer, hint: "Board builds, queued chat delegates, audits, workflow actors" },
  fleet_fast: { label: "Fast analyst", icon: Zap, hint: "Quick SQL answers · Sonnet or better", exclude: ["haiku"] },
  backend_tasks: { label: "One-shot LLM tasks", icon: Server, hint: "Feed digest · meeting summaries · feature checklists" },
};
const CHAT_CONSUMERS = ["chat_governance", "chat_engine", "chat_analyst"];

// Vendor → what a person needs to know: who runs it and how it is billed.
// A new vendor = one entry here (the backend keeps vendors as opaque ids).
const VENDOR_META = {
  bedrock: { label: "AWS Bedrock", billing: "API · pay per token", cls: "bg-amber-100 text-amber-800" },
  claude_code: { label: "Claude subscription", billing: "Claude Code CLI", cls: "bg-purple-100 text-purple-800" },
  codex: { label: "ChatGPT subscription", billing: "Codex CLI", cls: "bg-emerald-100 text-emerald-800" },
  openai: { label: "OpenAI API", billing: "pay per token", cls: "bg-emerald-100 text-emerald-800" },
};
const vendorMeta = (v) => VENDOR_META[v] || { label: v || "—", billing: "", cls: "bg-slate-100 text-slate-700" };

const fmt = (v) => {
  if (!v) return "";
  try { return new Date(v).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(v); }
};

// Relative age for heartbeat / snapshot timestamps (epoch seconds or ISO).
const ago = (v) => fmtAgo(v);
const fmtEpoch = (sec) => {
  if (!sec) return "";
  try { return new Date(sec * 1000).toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};
// Heartbeat is due every heartbeat_s (60s). A pod counts as alive for up to
// ALIVE_GRACE_MS without one: a backend roll drops a few beats in a row (the
// POST simply fails while the backend restarts), and flagging the fleet
// "silent" every deploy taught people to ignore the dot.
const ALIVE_GRACE_MS = 10 * 60 * 1000;
const isAlive = (row) => {
  const t = new Date(row.seen_at).getTime();
  const period = (row.heartbeat_s || 60) * 1000;
  return Date.now() - t < Math.max(period * 3, ALIVE_GRACE_MS);
};

// A route edit the pod has not picked up within this long is drift, not lag
// (the dispatcher re-reads routes every 60s).
const PENDING_GRACE_MS = 5 * 60 * 1000;

// Fleet rows: compare the registry with what live pods report applying.
//   applied  — every alive pod runs this model
//   pending  — a pod is alive but still on the previous model (edit < 5 min)
//   drift    — alive pod(s) run something else for 5+ min → the pod cannot
//              run this model (missing vendor adapter / login) — the signal
//              to watch after a vendor switch
//   nopod    — no alive pod; the route is recorded, nobody applies it
function routeStatus(route, statusRows) {
  if (!route.wired) return { kind: "unwired" };
  if (!route.consumer.startsWith("fleet_")) return { kind: "active" };
  const alive = statusRows.filter(isAlive);
  if (!alive.length) return { kind: "nopod" };
  const reporting = alive.filter((p) => p.routes && p.routes[route.consumer] != null);
  if (!reporting.length) return { kind: "active" }; // older dispatcher: no routes in heartbeat
  const off = reporting.filter((p) => p.routes[route.consumer] !== route.model_id);
  if (!off.length) {
    const latest = reporting.map((p) => new Date(p.seen_at).getTime()).sort((a, b) => a - b).pop();
    return { kind: "applied", seen_at: new Date(latest).toISOString() };
  }
  const editedAgo = route.updated_at ? Date.now() - new Date(route.updated_at).getTime() : Infinity;
  return { kind: editedAgo < PENDING_GRACE_MS ? "pending" : "drift", runs: off[0].routes[route.consumer] };
}

function StatusCell({ route, statusRows, labelOf }) {
  const s = routeStatus(route, statusRows);
  const c = route.consumer;
  if (s.kind === "unwired") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-page-muted" title="The consumer still uses its built-in default; this row takes effect when its phase is wired.">
        <CircleDashed size={13} /> not applied yet
      </span>
    );
  }
  // Two short lines: the verdict, then its detail — long single lines
  // overflowed the fixed-width column.
  const wrap = (cls, Icon, label, detail, title, extraTestId, spin) => (
    <span className="flex flex-col gap-0.5 text-xs items-start" data-testid={`fleet-wired-${c}`}>
      <Hint text={title} align="start">
        <span data-testid={extraTestId} className={`inline-flex items-center gap-1 whitespace-nowrap ${cls}`}>
          <Icon size={13} className={spin ? "animate-spin" : ""} /> {label}
        </span>
      </Hint>
      {detail && <span className="text-[11px] text-page-muted">{detail}</span>}
    </span>
  );
  switch (s.kind) {
    case "applied":
      return wrap("text-emerald-700", CircleCheck, "in use", `worker confirmed ${ago(s.seen_at)}`, "The worker reports running exactly this model. It re-reads the routing table about once a minute.", `fleet-applied-${c}`);
    case "pending":
      return wrap("text-amber-700", Loader2, "switching", "worker picks it up within a minute", `The worker still reports ${labelOf(s.runs)}. It re-reads the routing table about once a minute; this line turns green when it confirms the new model.`, `fleet-pending-${c}`, true);
    case "drift":
      return wrap("text-red-700", AlertTriangle, "not taken", `worker still runs ${labelOf(s.runs)} — check its adapter or login`, "The worker has not taken this model for over 5 minutes. Usually it cannot run this vendor at all: the adapter is missing or the account is not logged in on the worker.", `fleet-drift-${c}`);
    case "nopod":
      return wrap("text-page-muted", Radio, "no worker online", "applies when one comes up", "No worker has reported a heartbeat recently. The choice is saved and takes effect as soon as a worker is online.", `fleet-nopod-${c}`);
    default:
      return wrap("text-emerald-700", CircleCheck, "in use", null, "Read from the routing table on the consumer's next call — there is nothing to wait for.");
  }
}

// One vendor's limit windows as bars. `utilization` is a 0..1 fraction (the
// subscription-quota vendors); RPM/TPM-style vendors fill remaining/limit.
function LimitWindows({ entry }) {
  const windows = Array.isArray(entry.windows) ? entry.windows : [];
  const vm = vendorMeta(entry.vendor);
  return (
    <div className="flex flex-col gap-1.5 min-w-[260px]" data-testid={`fleet-limits-${entry.vendor}`}>
      <div className="flex items-center gap-2 text-xs text-page-muted">
        <span className={`px-1.5 py-0 rounded-full text-[10px] font-medium ${vm.cls}`}>{vm.label}</span>
        <span>{entry.account || "subscription"}</span>
        <span>· {entry.source === "probe" ? "probe" : "from jobs"} {ago(entry.captured_at)}</span>
        {entry.status && entry.status !== "allowed" && (
          <span className="text-amber-700 font-medium">{entry.status}</span>
        )}
      </div>
      {windows.length === 0 && <div className="text-xs text-page-muted">n/a</div>}
      {windows.map((w) => {
        const pct = typeof w.utilization === "number" ? Math.round(w.utilization * 100) : null;
        const color = pct === null ? "#94a3b8" : pct >= 90 ? "#dc2626" : pct >= 70 ? "#d97706" : "#16a34a";
        return (
          <div key={w.key} className="flex items-center gap-2 text-xs" data-testid="fleet-limit-window">
            <span className="w-14 text-page-muted">{w.label || w.key}</span>
            <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
              {pct !== null && <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, background: color }} />}
            </div>
            <span className="w-10 text-right font-medium text-page-title tabular-nums">
              {pct !== null ? `${pct}%` : (w.remaining != null && w.limit != null ? `${w.remaining}/${w.limit}` : "—")}
            </span>
            <span className="w-[120px] text-page-muted">{w.resets_at ? `resets ${fmtEpoch(w.resets_at)}` : ""}</span>
          </div>
        );
      })}
    </div>
  );
}

const STATUS_WORDS = { allowed_warning: { text: "close to the limit", cls: "text-amber-700" }, rejected: { text: "limit reached", cls: "text-red-700" } };
const WINDOW_NAMES = { "5h": "5-hour window", "7d": "7-day window", five_hour: "5-hour window", seven_day: "7-day window" };

// One subscription's limit windows with burn rate and forecast, from the
// heartbeat HISTORY (GET /api/fleet/models → quota). Rendered once per
// subscription, not per pod: N pods on one token share these numbers.
function QuotaBlock({ vendor, account, windows }) {
  const vm = vendorMeta(vendor);
  const status = windows.map((w) => w.status).find((s) => STATUS_WORDS[s]);
  return (
    <div className="border border-card-border rounded-[8px] bg-card-bg px-4 py-3 mb-2" data-testid={`fleet-limits-${vendor}`}>
      <div className="flex items-center gap-2 text-xs text-page-muted mb-2">
        <span className={`px-1.5 py-0 rounded-full text-[10px] font-medium ${vm.cls}`}>{vm.label}</span>
        <span>{(() => { const short = String(account || "").replace(/^Claude subscription\s*/i, "").replace(/^\((.*)\)$/, "$1").trim(); return short ? `${short} account` : "subscription"; })()}</span>
        <span>· updated {ago(Math.max(...windows.map((w) => w.captured_at || 0)))}</span>
        {status && (
          <Hint text={status === "rejected"
            ? "The provider is refusing requests: a window is used up. Jobs started now fail until it resets."
            : "The provider marks this subscription as close to one of its limits. Requests still go through; if a window fills up, new jobs fail until it resets."}>
            <span className={`font-medium ${STATUS_WORDS[status].cls}`} data-testid="fleet-quota-status">{STATUS_WORDS[status].text}</span>
          </Hint>
        )}
      </div>
      <div className="flex flex-col gap-2">
        {windows.map((w) => {
          const pct = Math.round((w.utilization || 0) * 100);
          const color = pct >= 90 ? "#dc2626" : pct >= 70 ? "#d97706" : "#16a34a";
          // No forecast line: "at this pace about N% by then" was a straight-line
          // guess that read like a measurement (removed 2026-08-27). Rates and
          // forecasts stay in the API for anyone who wants them.
          const name = WINDOW_NAMES[w.window_key] || w.label;
          return (
            <div key={w.window_key} className="flex items-center gap-2 text-xs" data-testid="fleet-limit-window">
              <span className="w-[100px] text-page-muted">{name}</span>
              <div className="w-[160px] h-2 rounded-full bg-slate-200 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
              </div>
              <span className="w-[68px] font-medium text-page-title tabular-nums">{pct}% used</span>
              <span className="text-page-muted whitespace-nowrap">
                {w.hours_to_reset != null && (
                  <Hint text={`This ${name} resets at ${fmtClockAt(w.resets_at)}, your local time; its usage drops back to 0 then. It is an allowance of the whole subscription — every worker on this account draws from it.`}>
                    {`resets at ${fmtClockAt(w.resets_at)}`}<span className="opacity-70"> (in {fmtDur(w.hours_to_reset * 3600)})</span>
                  </Hint>
                )}
              </span>

            </div>
          );
        })}
      </div>
    </div>
  );
}

// Model picker grouped by vendor. Offers enabled models the consumer may run
// (vendor allow-list, chat flag for chat consumers, per-consumer excludes);
// the current model always stays listed so a legacy incompatible route is
// visible — and flagged — not hidden.
function ModelSelect({ value, catalog, allowed, chatOnly, exclude, onChange, disabled, testid }) {
  const offered = catalog.filter((m) =>
    m.id === value || (m.enabled && (!allowed.length || allowed.includes(m.vendor)) && (!chatOnly || m.chat !== false) && !(exclude || []).includes(m.id)));
  const groups = [];
  for (const m of offered) {
    let g = groups.find((x) => x.vendor === m.vendor);
    if (!g) { g = { vendor: m.vendor, items: [] }; groups.push(g); }
    g.items.push(m);
  }
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      data-testid={testid}
      className="h-8 px-2 rounded-[5px] border border-card-border bg-page-bg text-page-title text-sm w-full max-w-[300px]"
    >
      {groups.map((g) => (
        <optgroup key={g.vendor} label={`${vendorMeta(g.vendor).label} · ${vendorMeta(g.vendor).billing}`}>
          {g.items.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

export default function Fleet() {
  const [routes, setRoutes] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [status, setStatus] = useState([]); // fleet_status rows: worker pods' heartbeats
  const [quota, setQuota] = useState([]); // per-subscription windows with burn rate + forecast
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null); // consumer (or "chat") being saved
  const [chatExpanded, setChatExpanded] = useState(false);
  const [heavy, setHeavy] = useState(null); // GET /api/fleet/heavy payload
  const [heavyError, setHeavyError] = useState(null);

  const loadHeavy = async () => {
    try {
      setHeavy(await getFleetHeavy());
      setHeavyError(null);
    } catch (e) {
      setHeavyError(e?.message || "Failed to load the heavy lane");
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getFleetModels();
      setRoutes(res.routes || []);
      setCatalog(res.catalog || []);
      setStatus(res.status || []);
      setQuota(res.quota || []);
    } catch (e) {
      setError(e?.message || "Failed to load the model registry");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  // Live swarm: poll while the tab is visible; a hidden tab skips ticks so a
  // page left open overnight doesn't hammer the API.
  useEffect(() => {
    loadHeavy();
    const t = setInterval(() => { if (document.visibilityState === "visible") loadHeavy(); }, HEAVY_POLL_MS);
    return () => clearInterval(t);
  }, []);
  // Status "applied by pod" depends on the heartbeat; refresh the registry
  // (cheap) on the same cadence while an edit is settling.
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      const settling = routes.some((r) => ["pending", "drift"].includes(routeStatus(r, status).kind));
      if (settling) getFleetModels().then((res) => { setStatus(res.status || []); setQuota(res.quota || []); }).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [routes, status]);

  const modelOf = (id) => catalog.find((m) => m.id === id);
  const vendorOf = (id) => modelOf(id)?.vendor || "";
  const labelOf = (id) => modelOf(id)?.label || id;

  // Switching VENDOR is the consequential move on this page: confirm it, in
  // plain words, before the PUT. Same-vendor model swaps go straight through.
  const confirmVendorChange = (consumers, fromId, toId) => {
    const from = vendorOf(fromId), to = vendorOf(toId);
    if (!from || !to || from === to) return true;
    const what = consumers.map((c) => (CONSUMER_META[c] || { label: c }).label).join(", ");
    const tv = vendorMeta(to);
    const fleet = consumers.some((c) => c.startsWith("fleet_"));
    return window.confirm(
      `Switch "${what}" from ${vendorMeta(from).label} to ${tv.label} (${tv.billing})?\n\n` +
      (fleet
        ? `The worker pod must have this vendor's adapter and login; the Status column shows "applied by pod" once it has picked the route up (~60s), or flags drift if it cannot run it.`
        : `The consumer reads the registry on its next call.`),
    );
  };

  const putRoutes = async (consumers, modelId, savingKey) => {
    setSaving(savingKey);
    setError(null);
    const prev = routes;
    // Optimistic: the select reflects the choice immediately; a failed PUT
    // restores the previous rows and surfaces the error.
    setRoutes((rs) => rs.map((r) => (consumers.includes(r.consumer) ? { ...r, model_id: modelId } : r)));
    try {
      const results = await Promise.all(consumers.map((c) => setModelRoute(c, modelId)));
      setRoutes((rs) => rs.map((r) => {
        const i = consumers.indexOf(r.consumer);
        return i >= 0 ? { ...r, ...results[i].route } : r;
      }));
    } catch (e) {
      setRoutes(prev);
      setError(e?.message || `Failed to update ${consumers.join(", ")}`);
    } finally {
      setSaving(null);
    }
  };

  const onChange = (route, modelId) => {
    if (modelId === route.model_id) return;
    if (!confirmVendorChange([route.consumer], route.model_id, modelId)) return;
    putRoutes([route.consumer], modelId, route.consumer);
  };

  const chatRoutes = routes.filter((r) => CHAT_CONSUMERS.includes(r.consumer));
  const chatAllEqual = chatRoutes.length > 0 && chatRoutes.every((r) => r.model_id === chatRoutes[0].model_id);
  const chatFolded = chatAllEqual && !chatExpanded;
  const onChatChange = (modelId) => {
    if (modelId === chatRoutes[0].model_id) return;
    if (!confirmVendorChange(CHAT_CONSUMERS, chatRoutes[0].model_id, modelId)) return;
    putRoutes(chatRoutes.map((r) => r.consumer), modelId, "chat");
  };

  const renderRow = (r) => {
    const meta = CONSUMER_META[r.consumer] || { label: r.consumer, icon: Bot, hint: r.description || "" };
    const Icon = meta.icon;
    const vendor = vendorOf(r.model_id);
    const vm = vendorMeta(vendor);
    const allowed = Array.isArray(r.allowed_vendors) ? r.allowed_vendors : [];
    const incompatible = !!vendor && allowed.length > 0 && !allowed.includes(vendor);
    return (
      <tr key={r.consumer} className="border-t border-card-border" data-testid={`fleet-route-${r.consumer}`}>
        <td className="px-4 py-3 align-top">
          <div className="flex items-center gap-2 font-medium text-page-title">
            <Icon size={15} className="shrink-0 text-page-muted" /> {meta.label}
          </div>
          <div className="text-xs text-page-muted mt-0.5">{meta.hint || r.description}</div>
        </td>
        <td className="px-4 py-3 align-top">
          <div className="flex items-center gap-2">
            <ModelSelect
              value={r.model_id} catalog={catalog} allowed={allowed}
              chatOnly={r.consumer.startsWith("chat_")} exclude={meta.exclude}
              onChange={(v) => onChange(r, v)} disabled={saving === r.consumer || saving === "chat"}
              testid={`fleet-model-select-${r.consumer}`}
            />
            {saving === r.consumer && <Loader2 size={14} className="animate-spin text-page-muted" />}
          </div>
        </td>
        <td className="px-4 py-3 align-top">
          {vendor && (
            <div className="whitespace-nowrap">
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${vm.cls}`} data-testid={`fleet-vendor-${r.consumer}`}>{vm.label}</span>
              <div className="text-[11px] text-page-muted mt-1">{vm.billing}</div>
            </div>
          )}
          {incompatible && (
            <div className="text-[11px] text-red-700 mt-1" data-testid={`fleet-incompatible-${r.consumer}`}
                 title={`This consumer runs ${allowed.join(" / ")} models; the routed one is ignored downstream.`}>
              not runnable here — pick a {allowed.map((a) => vendorMeta(a).label).join(" / ")} model
            </div>
          )}
        </td>
        <td className="px-4 py-3 align-top"><StatusCell route={r} statusRows={status} labelOf={labelOf} /></td>
        <td className="px-4 py-3 align-top text-xs text-page-muted">
          {r.updated_by ? (<><div className="whitespace-nowrap">{fmt(r.updated_at)}</div><div className="truncate" title={r.updated_by}>{r.updated_by.split("@")[0]}</div></>) : <span className="opacity-70">never changed</span>}
        </td>
      </tr>
    );
  };

  // The folded chat row: one select that writes to all three chat routes.
  const renderChatFolded = () => {
    const r = chatRoutes[0];
    const vendor = vendorOf(r.model_id);
    const vm = vendorMeta(vendor);
    const allowed = Array.isArray(r.allowed_vendors) ? r.allowed_vendors : [];
    return (
      <tr key="chat" className="border-t border-card-border" data-testid="fleet-route-chat">
        <td className="px-4 py-3 align-top">
          <div className="flex items-center gap-2 font-medium text-page-title">
            <MessageSquare size={15} className="shrink-0 text-page-muted" /> Default model for chat
          </div>
          <div className="text-xs text-page-muted mt-0.5">Main, engine and analyst chats</div>
          <button type="button" onClick={() => setChatExpanded(true)} data-testid="fleet-chat-customize"
                  className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-page-muted hover:text-page-title bg-transparent border-0 p-0 cursor-pointer">
            <ChevronRight size={11} /> customize per persona
          </button>
        </td>
        <td className="px-4 py-3 align-top">
          <div className="flex items-center gap-2">
            <ModelSelect value={r.model_id} catalog={catalog} allowed={allowed} chatOnly onChange={onChatChange}
                         disabled={saving === "chat"} testid="fleet-model-select-chat" />
            {saving === "chat" && <Loader2 size={14} className="animate-spin text-page-muted" />}
          </div>
        </td>
        <td className="px-4 py-3 align-top">
          {vendor && (<div className="whitespace-nowrap">
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${vm.cls}`} data-testid="fleet-vendor-chat">{vm.label}</span>
            <div className="text-[11px] text-page-muted mt-1">{vm.billing}</div>
          </div>)}
        </td>
        <td className="px-4 py-3 align-top"><StatusCell route={r} statusRows={status} labelOf={labelOf} /></td>
        <td className="px-4 py-3 align-top text-xs text-page-muted">
          {(() => { const last = chatRoutes.filter((x) => x.updated_by).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0];
            return last ? (<><div className="whitespace-nowrap">{fmt(last.updated_at)}</div><div className="truncate" title={last.updated_by}>{last.updated_by.split("@")[0]}</div></>) : <span className="opacity-70">never changed</span>; })()}
        </td>
      </tr>
    );
  };

  const sectionRows = (section) => {
    const claimed = new Set();
    for (const s of SECTIONS) { if (s === section) break; routes.forEach((r) => { if (s.match(r.consumer)) claimed.add(r.consumer); }); }
    return routes.filter((r) => !claimed.has(r.consumer) && section.match(r.consumer));
  };

  return (
    <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full" data-testid="fleet-page">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-semibold text-page-title flex items-center gap-2">
          <Bot size={20} /> Agent fleet
        </h1>
        <button
          type="button"
          onClick={() => { load(); loadHeavy(); }}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-[5px] text-sm text-page-muted hover:text-page-title border border-card-border bg-transparent cursor-pointer"
          title="Reload"
        >
          <RefreshCw size={14} /> Reload
        </button>
      </div>
      <p className="text-sm text-page-muted mb-5">
        What the fleet is working on right now, the worker pods and their subscription limits, and which
        model — and vendor — each part of the platform runs on.
      </p>

      {error && (
        <div className="mb-4 px-3 py-2 rounded-[5px] bg-red-50 text-red-700 text-sm" data-testid="fleet-error">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-page-muted text-sm py-10">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      ) : (
        <div className="max-w-[980px] mb-6">
          <h2 className="text-sm font-medium text-page-title mb-2">Models &amp; vendors</h2>
          <div className="border border-card-border rounded-[8px] overflow-hidden bg-card-bg" data-testid="fleet-routes">
            <table className="w-full text-sm border-collapse table-fixed">
              <colgroup>
                <col style={{ width: "28%" }} /><col style={{ width: "27%" }} /><col style={{ width: "16%" }} /><col style={{ width: "15%" }} /><col style={{ width: "14%" }} />
              </colgroup>
              <thead>
                <tr className="bg-table-header text-left text-page-muted">
                  <th className="px-4 py-2.5 font-medium">Where it runs · what</th>
                  <th className="px-4 py-2.5 font-medium">Model</th>
                  <th className="px-4 py-2.5 font-medium">Runs on</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 font-medium">Changed</th>
                </tr>
              </thead>
              <tbody>
                {SECTIONS.map((section) => {
                  const rows = sectionRows(section);
                  if (!rows.length) return null;
                  return (
                    <React.Fragment key={section.key}>
                      <tr className="border-t border-card-border bg-page-bg" data-testid={`fleet-section-${section.key}`}>
                        <td colSpan={5} className="px-4 py-1.5">
                          <span className="text-[11px] uppercase tracking-wide font-medium text-page-title">{section.title}</span>
                          {section.note && <span className="text-[11px] text-page-muted ml-2">— {section.note}</span>}
                          {section.key === "chat" && chatAllEqual && chatExpanded && (
                            <button type="button" onClick={() => setChatExpanded(false)} data-testid="fleet-chat-collapse"
                                    className="ml-3 inline-flex items-center gap-1 text-[11px] text-page-muted hover:text-page-title bg-transparent border-0 p-0 cursor-pointer">
                              <ChevronDown size={11} /> one model for all
                            </button>
                          )}
                          {section.key === "chat" && !chatAllEqual && (
                            <span className="text-[11px] text-amber-700 ml-2" data-testid="fleet-chat-mixed">personas run different models</span>
                          )}
                        </td>
                      </tr>
                      {section.key === "chat" && chatFolded ? renderChatFolded() : rows.map(renderRow)}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && (
        <div className="mb-6 max-w-[980px]" data-testid="fleet-workers">
          <h2 className="text-sm font-medium text-page-title mb-2">Fleet workers</h2>
          {(() => {
            // Group quota windows by subscription (vendor + account).
            const groups = new Map();
            for (const w of quota) { const k = `${w.vendor}|${w.account}`; if (!groups.has(k)) groups.set(k, []); groups.get(k).push(w); }
            return [...groups.entries()].map(([k, ws]) => {
              const [vendor, account] = k.split("|");
              ws.sort((a, b) => (a.window_key === "five_hour" ? -1 : 1) - (b.window_key === "five_hour" ? -1 : 1));
              return <QuotaBlock key={k} vendor={vendor} account={account} windows={ws} />;
            });
          })()}
          {status.length === 0 ? (
            <div className="border border-card-border rounded-[8px] bg-card-bg px-4 py-3 text-sm text-page-muted" data-testid="fleet-workers-empty">
              No heartbeat yet — a worker pod reports every minute once it runs a dispatcher with heartbeat support.
            </div>
          ) : status.map((row) => {
            const alive = isAlive(row);
            return (
              <div key={row.pod} className="border border-card-border rounded-[8px] bg-card-bg px-4 py-3 mb-2 flex flex-wrap gap-6 items-start" data-testid={`fleet-worker-${row.pod}`}>
                <div className="min-w-[240px]">
                  <div className="flex items-center gap-2 font-medium text-page-title">
                    <span className="inline-block size-2 rounded-full" style={{ background: alive ? "#16a34a" : "#9ca3af" }} />
                    <span>Worker</span>
                    <Hint text="The Kubernetes pod name: deployment · configuration version · instance. The version part changes on every fleet redeploy, the instance part on every restart — so a different name means a different pod, and requests claimed by an old name are orphaned.">
                      <span className="font-mono text-xs text-page-muted">{row.pod}</span>
                    </Hint>
                  </div>
                  <div className="text-xs text-page-muted mt-0.5">
                    {alive
                      ? <>online · heartbeat {ago(row.seen_at)}</>
                      : <>offline — no heartbeat for {fmtDur((Date.now() - new Date(row.seen_at).getTime()) / 1000)} · <Hint text="The worker has not reported for over 10 minutes. It is down or restarting; requests it had claimed are re-queued when a worker starts, except those waiting on a person.">worker down or restarting</Hint></>}
                  </div>
                </div>
                {/* Per-pod limits only until the history-based quota block has data. */}
                {quota.length === 0 && (Array.isArray(row.limits) ? row.limits : []).map((entry) => (
                  <LimitWindows key={entry.vendor} entry={entry} />
                ))}
              </div>
            );
          })}
        </div>
      )}

      <HeavySwarm data={heavy} error={heavyError} onChanged={loadHeavy} />

      {!loading && (
        <div className="mt-6">
          <h2 className="text-sm font-medium text-page-title mb-2">Model catalog</h2>
          <div className="flex flex-wrap gap-2" data-testid="fleet-catalog">
            {catalog.map((m) => (
              <span key={m.id} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border border-card-border ${m.enabled ? "text-page-title" : "text-page-muted line-through"}`}>
                {m.label}
                <span className={`px-1.5 py-0 rounded-full text-[10px] font-medium ${vendorMeta(m.vendor).cls}`}>{vendorMeta(m.vendor).label}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
